import { camelizeKeys } from '../lib/functions';

const emptyManifest = manifest => manifest.hot.total === 0
  && manifest.hot.target === 0
  && manifest.warm.total === 0
  && manifest.warm.target === 0;
;

export default class DataImportService {
  constructor(logger, eventBus, store, dao, config, deviceFactory) {
    this.logger = logger;
    this.eventBus = eventBus;
    this.store = store;
    this.dao = dao;
    this.config = config;
    this.deviceFactory = deviceFactory;
    this.pollHandles = {};

    this.initialize();
  }

  initialize() {
    this.eventBus.on(
      this.eventBus.EVENT_IMPORT_PROGRESS,
      event => this.handleImportProgress(event),
    );
    this.eventBus.on(
      this.eventBus.EVENT_POST_IMPORT_CALCULATIONS_COMPLETE,
      event => this.handlePostImportCalculationsComplete(event),
    );
    this.eventBus.on(
      this.eventBus.EVENT_POST_IMPORT_CALCULATIONS_PROGRESS,
      event => this.handlePostImportCalculationsProgress(event),
    );
    this.eventBus.on(this.eventBus.EVENT_CALENDAR_UPDATED, () => this.store.dispatch('loadData'));

    [
      this.eventBus.EVENT_BAD_SOURCE_CREDENTIALS,
      this.eventBus.EVENT_ACCOUNT_LOCKED,
      this.eventBus.EVENT_PASSWORD_RESET_REQUIRED,
      this.eventBus.EVENT_PULLER_FAILED,
    ].forEach(event => this.eventBus.on(event, e => this.handleError(e)));
  }

  startPoller(memberId) {
    if (!this.pollHandles[memberId]) {
      let appleAttempts = 0;

      this.pollHandles[memberId] = setInterval(async () => {
        console.log('Polling import status');

        let status;
        try {
          status = camelizeKeys((await this.dao.getImportStatus(memberId)).data);
        } catch (e) {
          if (e.response && e.response.status === 404) {
            console.log("Import doesn't exist...yet");
            return;
          }
          throw e;
        }

        console.log('Got status', status);
        if (status.hot.target === 0 && status.warm.target === 0) {
          console.log('Not enough data yet... not committing any changes.');
          return;
        }

        if (status.hot.target === 0 && window.platform && window.platform === 'ios') {
          appleAttempts += 1;
          if (appleAttempts < 10) {
            console.log('No hot data yet for apple... waiting a little longer.');
            return;
          }
        }

        this.store.commit('setImportProgress', {
          id: status.importId,
          target: status.hot.target,
          count: status.hot.total,
        });
        this.store.commit('setProcessingProgress', {
          id: status.importId,
          target: status.hot.processed.target,
          count: status.hot.processed.total,
        });
        if (status.hot.total >= status.hot.target
            && status.hot.processed.total >= status.hot.processed.target
            && !emptyManifest(status)) {
          this.store.dispatch('setProcessed', {
            id: status.importId,
            temperature: 'hot',
          });
        }
        if (status.hot.total >= status.hot.target
          && status.warm.processed.target !== null
          && status.warm.processed.total >= status.warm.processed.target
          && !emptyManifest(status)) {
          this.store.dispatch('setProcessed', {
            id: status.importId,
            temperature: 'warm',
          });
          console.log('Stopping poller');
          clearTimeout(this.pollHandles[memberId]);
          delete this.pollHandles[memberId];
        }
      }, 5000);
    }
  }

  handleImportProgress(event) {
    this.store.commit('setImportProgress', {
      id: event['_body']['import_id'], // eslint-disable-line
      target: event['_body']['target'], // eslint-disable-line
      count: event['_body']['count'], // eslint-disable-line
    });
  }

  handlePostImportCalculationsProgress(event) {
    this.store.commit('setProcessingProgress', {
      id: event['_body']['import_id'], // eslint-disable-line
      target: event['_body']['target'], // eslint-disable-line
      count: event['_body']['count'], // eslint-disable-line
    });
  }

  handlePostImportCalculationsComplete(event) {
    this.store.dispatch('setProcessed', {
      id: event['_body']['import_id'], // eslint-disable-line
      temperature: event['_body']['temperature'], // eslint-disable-line
    });
  }

  handleError(event) {
    let error;
    switch (event['_headers']['event_name']) {  // eslint-disable-line
      case this.eventBus.EVENT_BAD_SOURCE_CREDENTIALS:
        error = 'Incorrect email or password.';
        break;
      case this.eventBus.EVENT_ACCOUNT_LOCKED:
        error = 'Your account is currently locked. Please visit your device manufacturer\'s web site to reset it.';
        break;
      case this.eventBus.EVENT_PASSWORD_RESET_REQUIRED:
        error = 'Your account requires a password reset. Please visit your device manufacturer\'s web site.';
        break;
      default:
        error = 'The import failed for an unknown reason. Please try again later.';
        break;
    }

    this.store.dispatch('setError', {
      id: event['_body']['import_id'],  // eslint-disable-line
      error,
    });
  }

  async startSuuntoPuller(memberId) {
    try {
      const result = await this.dao.requestSuuntoActivityRefresh({
        params: {
          members: [
            { member_id: memberId, use_most_recent_activity_date: true },
          ],
        },
      });

      this.store.dispatch('addImport', result.data);
      return result.data;
    } catch (e) {
      this.logger.capture(e);
      return false;
    }
  }

  async startPuller(vendor, memberId, credentials = {}) {
    try {
      const data = Object.assign(credentials, {
        member_id: memberId,
        activity_source: vendor,
      });
      const result = await this.dao.httpPost(`vendor/${vendor}/pull`, data);
      if (result.data !== 'none') {
        this.store.dispatch('addImport', result.data);
        return result.data;
      }
      return null;
    } catch (e) {
      this.logger.capture(e);
      return false;
    }
  }

  async validateCredentials(vendor, credentials) {
    return this.dao.httpPost(`vendor/${vendor}/validate-credentials`, credentials);
  }

  async syncProfileData(vendor, email, password, memberId) {
    const user = await this.dao.httpPost(
      `member/${memberId}/sync-profile/${vendor}`,
      { email, password },
    );
    this.store.dispatch('mapAndSetUserProperties', user.data);
  }

  getLinkDeviceUrl(deviceType) {
    return this.deviceFactory.create(deviceType).linkUrl;
  }
}
